import { createElement } from '../../core/dom/helpers.js';
import { createInMemoryUrlState } from '../../core/routing/in-memory-url-state.js';
import { createFileBrowser } from './file-browser.js';
import { textFilterDriver } from '../../modules/filter-bar/drivers/text-filter-driver.js';
import { selectFilterDriver } from '../../modules/filter-bar/drivers/select-filter-driver.js';
import { FILE_KINDS } from '../../core/media/file-metadata.js';
import { buildAcceptMatcher, normalizeForAccept } from '../../core/files/accept-matcher.js';
import { isAbortError } from '../../core/http/is-abort-error.js';

// ---------------------------------------------------------------------------
// In-editor file-picker modal — mounts the reusable file-browser core in an
// overlay so a CRUD form file field can browse + upload + insert WITHOUT
// navigating away (the editor + unsaved edits survive).
// ---------------------------------------------------------------------------
//
// Lives in features/ beside the file-browser core it mounts; the file-field
// driver (a component) reaches it only via an injected, app-bound opener (a
// component may not import features). Returns `{ promise, destroy }`; the
// promise resolves with the chosen FileMeta[] (or null on cancel/Escape/
// backdrop/abort/destroy).
//
// Modal-owned abort: a single internal AbortController (`modalAbort`) is passed
// to every async call (core list + upload + persist). cleanup() aborts it on ANY
// close path, so closing mid-upload cancels in-flight work (no orphan /files row,
// no post-close refresh/insert). The external opts.signal only TRIGGERS cleanup.
//
// Escape is registered in the CAPTURE phase (and stopPropagation'd) so it closes
// only the picker — the parent crud-editor's bubble-phase Escape never sees it.

/**
 * @typedef {import('../../contracts/file-selection.js').FileMeta} FileMeta
 * @typedef {import('../../contracts/file-selection.js').FileBrowseClient} FileBrowseClient
 * @typedef {import('../../contracts/upload.js').UploadClient} UploadClient
 */

/** Picker filters: name search + Type select ONLY (no portaling tags/daterange). */
const PICKER_FILTERS = [
  { key: 'name', label: 'Search by file name', type: 'text', placeholder: 'Search by file name…', span: 4 },
  {
    key: 'kind', label: 'Type', type: 'select',
    options: FILE_KINDS.map(k => ({ value: k, label: k.charAt(0).toUpperCase() + k.slice(1) })),
  },
];

/**
 * @typedef {Object} FilePickerOptions
 * @property {FileBrowseClient} browseClient - Browse (list) + optional persistUpload (for the modal's upload).
 * @property {UploadClient} [uploadClient] - Byte upload; enables the modal's Upload control (with persistUpload).
 * @property {string} [accept] - Restrict selectable + uploadable files (e.g. 'image/*').
 * @property {boolean} [multiple] - Allow selecting more than one file. Default true.
 * @property {AbortSignal} [signal] - External signal (the driver's session controller); aborting it closes the modal.
 * @property {{ emit: (name: string, data?: unknown) => void }} eventBus - For failure toasts (app-bound).
 * @property {{ debug: Function, info: Function, warn: Function, error: Function }} logger - App-bound.
 * @property {import('../../contracts/locale.js').LocaleConfig} [localeConfig] - App-bound (for the core's date column).
 */

/**
 * Open the in-editor file picker.
 *
 * @param {FilePickerOptions} opts
 * @returns {{ promise: Promise<FileMeta[] | null>, destroy: () => void }}
 */
export function openFilePicker(opts) {
  const { browseClient, uploadClient, accept = '', multiple = true, signal, eventBus, logger, localeConfig } = opts;

  const log = logger;
  /** Single internal abort: every async call gets this; cleanup() aborts it. */
  const modalAbort = new AbortController();
  let settled = false;
  /** @type {(value: FileMeta[] | null) => void} */
  let resolveFn = () => {};
  const promise = /** @type {Promise<FileMeta[] | null>} */ (
    new Promise((resolve) => { resolveFn = resolve; })
  );

  const acceptMatcher = buildAcceptMatcher(accept);
  const canUpload = !!(uploadClient && browseClient.persistUpload);

  // --- DOM scaffold (overlay/panel modeled on tag-editor) ---
  const overlay = createElement('div', { class: 'file-picker-modal__overlay' });
  const panel = createElement('div', {
    class: 'file-picker-modal__panel', role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Select file',
  });
  // Header bar: title + a corner × close (mirrors the crud-editor modal). Replaces
  // the old in-header Cancel button — × is the single close affordance (plus Escape).
  const titleEl = createElement('h3', { class: 'file-picker-modal__title' }, ['Select file']);
  const closeBtn = createElement('button', {
    type: 'button', class: 'file-picker-modal__close', 'aria-label': 'Close',
  }, ['×']);
  closeBtn.addEventListener('click', () => close(null));
  const header = createElement('div', { class: 'file-picker-modal__header' });
  header.appendChild(titleEl);
  header.appendChild(closeBtn);

  // Header extras for the core's header row: the modal's own Upload control (distinct
  // from the field's native "Upload from computer"). No Cancel — the × in the panel
  // header closes the modal.
  /** @type {HTMLElement[]} */
  const headerExtras = [];
  /** @type {HTMLInputElement | null} */
  let uploadInput = null;
  if (canUpload) {
    const uploadBtn = createElement('button', { type: 'button', class: 'btn file-picker-modal__upload' }, ['Upload']);
    /** @type {Record<string, string>} */
    const inputAttrs = { type: 'file', class: 'file-picker-modal__upload-input', style: 'display:none' };
    if (accept) inputAttrs.accept = accept;
    if (multiple) inputAttrs.multiple = 'multiple';
    uploadInput = /** @type {HTMLInputElement} */ (createElement('input', inputAttrs));
    uploadBtn.addEventListener('click', () => uploadInput?.click());
    uploadInput.addEventListener('change', onUploadChange);
    headerExtras.push(uploadBtn, uploadInput);
  }

  const errorEl = createElement('div', { class: 'file-picker-modal__error', role: 'alert', 'aria-live': 'polite' });
  errorEl.hidden = true;

  const body = createElement('div', { class: 'file-picker-modal__body' });

  panel.appendChild(header);
  panel.appendChild(errorEl);
  panel.appendChild(body);
  overlay.appendChild(panel);
  document.body.appendChild(overlay);

  const previousFocus = /** @type {HTMLElement | null} */ (document.activeElement);
  const savedOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';

  /** @param {string} msg */
  function showError(msg) { errorEl.textContent = msg; errorEl.hidden = false; }
  function clearError() { errorEl.textContent = ''; errorEl.hidden = true; }

  // --- Mount the core into the panel body ---
  const browser = createFileBrowser(body, {
    browseClient,
    filters: /** @type {any} */ (PICKER_FILTERS),
    filterDrivers: [textFilterDriver, selectFilterDriver],
    urlState: createInMemoryUrlState({ unorderedKeys: new Set(['tags']) }),
    logger: /** @type {any} */ (logger),
    eventBus,
    signal: modalAbort.signal,
    localeConfig,
    selectable: true,
    multiple,
    accept,
    persistSelectionAcrossQueries: true, // picks survive filter/page/sort/view in the modal
    chromeless: true, // the modal panel is the card — don't double it with page-container chrome
    rowActions: [], // no admin actions in pick mode
    bulkActions: [{
      label: 'Insert selected',
      variant: 'primary',
      onClick: (rows) => close(rows),
    }],
    initialView: 'table',
    persistView: false,
    showViewToggle: true,
    headerExtras,
  });

  // --- Upload-and-attach (modal's own Upload control) ---
  function onUploadChange() {
    if (!uploadInput || !uploadClient || !browseClient.persistUpload) return;
    const files = [...(uploadInput.files ?? [])];
    uploadInput.value = '';
    if (files.length === 0) return;
    // Single-field guard: the input has no `multiple` attr when !multiple, but an
    // OS "select all" can still hand us several files. In single mode the first
    // success would `close([...])` and abort the rest — so reject up front instead.
    if (!multiple && files.length > 1) {
      showError('Only one file can be added here.');
      return;
    }
    const persistUpload = browseClient.persistUpload;
    // Partition up front so a later accepted file's clearError() can't erase the
    // rejection notice for an earlier skipped one (mixed [bad.pdf, good.jpg]).
    const acceptedFiles = files.filter((f) => acceptMatcher(normalizeForAccept(f)));
    const rejectedCount = files.length - acceptedFiles.length;
    if (rejectedCount > 0) {
      log.warn('Rejected file type(s)', { data: { rejectedCount, accept } });
      showError(rejectedCount === files.length
        ? 'That file type is not allowed here.'
        : 'Some files were not allowed and were skipped.');
    } else {
      clearError();
    }
    if (acceptedFiles.length === 0) return;
    for (const file of acceptedFiles) {
      log.info('File upload start', { data: { name: file.name } });
      uploadClient.upload(file, { signal: modalAbort.signal })
        .then((uploaded) => {
          // Don't proceed to persist if the modal closed mid-upload — avoids an
          // orphan /files row (the upload bytes may exist, but we don't catalogue).
          if (modalAbort.signal.aborted || settled) return null;
          return persistUpload(uploaded, file, { signal: modalAbort.signal });
        })
        .then((fileMeta) => {
          if (!fileMeta || modalAbort.signal.aborted || settled) return;
          log.info('File upload success', { data: { id: fileMeta.id, name: file.name } });
          if (multiple) {
            // Add to the core's pending selection (NO refresh — refresh would
            // prune the user's existing grid picks). The union carries it.
            browser.addSelectedRow(fileMeta);
          } else {
            // Single field: attach immediately.
            close([fileMeta]);
          }
        })
        .catch((/** @type {unknown} */ err) => {
          if (modalAbort.signal.aborted || settled) return;
          if (isAbortError(err)) return;
          log.error('File upload failed', { data: err });
          eventBus.emit('toast.message.requested', { message: 'Upload failed', type: 'error' });
        });
    }
  }

  // --- Lifecycle ---
  /** @param {FileMeta[] | null} result */
  function close(result) {
    if (settled) return;
    settled = true;
    modalAbort.abort();
    document.body.style.overflow = savedOverflow;
    document.removeEventListener('keydown', onKeydown, true);
    if (signal) signal.removeEventListener('abort', onExternalAbort);
    browser.destroy();
    overlay.remove();
    if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus();
    resolveFn(result);
  }

  function onExternalAbort() { close(null); }

  /** @param {KeyboardEvent} e */
  function onKeydown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation(); // capture-phase: parent crud-editor's bubble Escape never sees it
      close(null);
      return;
    }
    if (e.key === 'Tab') {
      const focusable = /** @type {HTMLElement[]} */ (
        [...panel.querySelectorAll('input:not(:disabled), button:not(:disabled), select:not(:disabled), [tabindex]:not([tabindex="-1"]):not(:disabled)')]
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); e.stopPropagation(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); e.stopPropagation(); first.focus(); }
    }
  }

  // (× close button wired at creation, above.)
  overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) close(null); });
  // Capture phase + same flag on removal (mirrors media-picker).
  document.addEventListener('keydown', onKeydown, true);
  if (signal) {
    if (signal.aborted) { close(null); }
    else signal.addEventListener('abort', onExternalAbort);
  }

  return {
    promise,
    destroy() { close(null); },
  };
}

export default openFilePicker;
